import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BellRing, BellOff, Smartphone, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EVENT_OPTIONS = [
  { id: 'messages', label: 'Mensajes nuevos de clientes' },
  { id: 'escalations', label: 'Escalaciones y tickets urgentes' },
  { id: 'deals', label: 'Ventas y pagos' },
];

const urlBase64ToUint8Array = (value) => {
  const pad = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + pad).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
};

export const PushToggle = () => {
  const supported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState({ enabled: false, events: ['messages', 'escalations'], devices: 0 });

  useEffect(() => {
    axios.get(`${API}/push/settings`).then(r => setSettings(r.data)).catch(() => {});
    if (!supported) return;
    navigator.serviceWorker.ready
      .then(reg => reg.pushManager.getSubscription())
      .then(sub => setSubscribed(!!sub))
      .catch(() => {});
  }, [supported]);

  const saveSettings = async (next) => {
    setSettings(prev => ({ ...prev, ...next }));
    try {
      await axios.put(`${API}/push/settings`, {
        enabled: next.enabled ?? settings.enabled,
        events: next.events ?? settings.events,
      });
    } catch { toast.error('No pude guardar las preferencias'); }
  };

  const enablePush = async () => {
    setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('No diste permiso para notificaciones. Actívalo en los ajustes del navegador.');
        setBusy(false);
        return;
      }
      const { data } = await axios.get(`${API}/push/public-key`);
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      await axios.post(`${API}/push/subscribe`, sub.toJSON());
      setSubscribed(true);
      await saveSettings({ enabled: true });
      const r = await axios.get(`${API}/push/settings`);
      setSettings(r.data);
      toast.success('Notificaciones push activadas en este dispositivo');
    } catch (e) {
      toast.error('No pude activar las push en este dispositivo');
    } finally { setBusy(false); }
  };

  const disablePush = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      await saveSettings({ enabled: false });
      toast.success('Push desactivadas en este dispositivo');
    } catch { toast.error('No pude desactivar las push'); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    try {
      const r = await axios.post(`${API}/push/test`);
      toast.success(`Push de prueba enviada a ${r.data.devices} dispositivo(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No pude enviar la prueba');
    }
  };

  const toggleEvent = (id) => {
    const events = settings.events?.includes(id)
      ? settings.events.filter(e => e !== id)
      : [...(settings.events || []), id];
    saveSettings({ events });
  };

  return (
    <div className="space-y-4" data-testid="push-toggle">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold flex items-center gap-2">
            {subscribed ? <BellRing className="w-4 h-4 text-brand-green" /> : <BellOff className="w-4 h-4 text-muted-foreground" />}
            Notificaciones en el celular
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Te avisamos aunque la app esté cerrada. En iPhone debes agregar DEAL a la pantalla de inicio primero.
          </p>
          {settings.devices > 0 && (
            <Badge variant="outline" className="mt-2 text-[10px] gap-1" data-testid="push-devices-count">
              <Smartphone className="w-3 h-3" /> {settings.devices} dispositivo(s) conectado(s)
            </Badge>
          )}
        </div>
        <Button
          data-testid="push-enable-btn"
          size="sm"
          variant={subscribed ? 'outline' : 'default'}
          disabled={!supported || busy}
          onClick={subscribed ? disablePush : enablePush}
        >
          {!supported ? 'No compatible' : subscribed ? 'Desactivar' : 'Activar'}
        </Button>
      </div>

      {subscribed && (
        <>
          <div className="space-y-2">
            {EVENT_OPTIONS.map(opt => (
              <label key={opt.id} className="flex items-center justify-between gap-3 py-1">
                <span className="text-xs sm:text-sm">{opt.label}</span>
                <Switch
                  data-testid={`push-event-${opt.id}`}
                  checked={!!settings.events?.includes(opt.id)}
                  onCheckedChange={() => toggleEvent(opt.id)}
                />
              </label>
            ))}
          </div>
          <Button data-testid="push-test-btn" variant="outline" size="sm" className="gap-1.5" onClick={sendTest}>
            <Send className="w-3.5 h-3.5" /> Enviar push de prueba
          </Button>
        </>
      )}
    </div>
  );
};
